/* eslint-disable react-hooks/exhaustive-deps */
import { useEffect, useRef, useState } from "react";
import { DialogWrapper } from "../common/dialogWrapper";
import { useTranslation } from "react-i18next";
import { Box } from "@mui/material";
import { FsButton } from "../button";
import {
  getSessionDeadline,
  getToken,
  Logout,
  refreshSession,
} from "../../../services/authService";
import { buildLocalizeRoute } from "../../../helpers/languageHelper";
import { AppRoutes } from "../../../constants/appRoutes";
import { TextWrapper } from "../formUtilities/useTextWrapper";

const WARNING_WINDOW_MS = 30000;

export const TokenExpiryWarningDialog = () => {
  const [secondsLeft, setSecondsLeft] = useState(null);
  const [extending, setExtending] = useState(false);
  const endedRef = useRef(false);

  const { t } = useTranslation();

  const endSession = () => {
    if (endedRef.current) {
      return;
    }
    endedRef.current = true;
    Logout();
    window.location.href = buildLocalizeRoute(AppRoutes.Login);
  };

  // One interval for the component's lifetime, never torn down on fire or dismiss.
  // The session renews every few minutes, so the warning has to be able to come back
  // rather than showing once per page load.
  useEffect(() => {
    const tick = () => {
      const deadline = getSessionDeadline();
      if (!deadline || !getToken()) {
        setSecondsLeft(null);
        return;
      }

      const remaining = deadline - Date.now();
      if (remaining <= 0) {
        // Sign out here instead of waiting for a 401: the user this dialog exists for
        // is idle by definition and will make no request to trigger one.
        endSession();
        return;
      }

      setSecondsLeft(
        remaining <= WARNING_WINDOW_MS ? Math.ceil(remaining / 1000) : null
      );
    };

    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, []);

  const onExtend = () => {
    setExtending(true);
    // Shares the 401 interceptor's in-flight promise, so a click landing alongside a
    // background refresh cannot spend the same token twice and trip reuse detection.
    refreshSession().then((accessToken) => {
      setExtending(false);
      if (!accessToken) {
        endSession();
      }
    });
  };

  return <DialogWrapper
    maxWidth="xs"
    state={secondsLeft !== null}
    preventOutsideClick={true}
    hideCloseBtn={true}
    title={t('warning')}
    content={
      <Box className="text-center">
        <TextWrapper
          classList="!text-center"
          text={t('sessionExpiryCountdown', { seconds: secondsLeft ?? 0 })} />
      </Box>
    }
    actions={
      <Box className="flex items-center gap-3">
        <FsButton variant="outline" disabled={extending} onClick={endSession}>
          {t('signOut')}
        </FsButton>
        <FsButton loading={extending} onClick={onExtend}>
          {t('staySignedIn')}
        </FsButton>
      </Box>
    }></DialogWrapper>;
};
